import { SendEmailRequest } from '@aws-sdk/client-ses'
import { PublishInput } from '@aws-sdk/client-sns'
import { IOTV_Alert_Trigger_EventType, IOTV_Notifiable_EventType, IOTV_Thing_EventType, MapLike } from '@iotv/iotv-v3-types'
import { ValidModelObjects, type ValidModelObject } from '../../classDependentTypes/ValidModelClasses'
import { AlertTrigger, Incident } from '../Incidents'
import { AlertIncidentType, Severity } from '../Incidents/enums'
import { IncidentStatus } from '../Incidents/IncidentTypes'
import { DeliveryType, MessagePlaceholders, SubscribedStatus } from './enums'

export type SubscriptionState = {
  status: SubscribedStatus
}

export type SubscribedStatusUnion = SubscriptionState | PostponedSubscriptionState | EventDependentSubscriptionState

export const isPostponedSubscriptionState = (subscriptionState: any): subscriptionState is PostponedSubscriptionState => {
  const castObject = subscriptionState as PostponedSubscriptionState
  return castObject.reapplyAfterDateMs !== undefined
}

export const isEventDependentSubscriptionState = (subscriptionState: any): subscriptionState is EventDependentSubscriptionState => {
  const castObject = subscriptionState as EventDependentSubscriptionState
  return castObject.reapplyAfterEvent !== undefined
}

export type PostponedSubscriptionState = SubscriptionState & {
  reapplyAfterDateMs: number
}

export type EventDependentSubscriptionState = SubscriptionState & {
  reapplyAfterEvent: IOTV_Alert_Trigger_EventType | IOTV_Thing_EventType
}

export type EscalationConfig = {
  /** The millisecond duration after which the incident should be open to generate escalated notifications */
  afterDurationMs?: number
  /** @deprecated The severity at or above which the incident should be to generate escalated notifications */
  atSeverity?: Severity
  /** A user to receive the escalated notification */
  recipient?: ValidModelObject<'User'>
}

export type NotificationDeliveryConfig = NotificationContent & {
  deliveryType: DeliveryType
  /** A list of users to receive notifications */
  recipients: ValidModelObjects<'User' | 'Customer'>
  /** A list of other destinations (e.g. email addresses) to receive notifications */
  destinations: string[]
  /** Reference key will be used as `pk` of notification, if present */
  referenceKey?: string
  /** @deprecated the escalation person */
  escalation?: EscalationConfig
  /** The list of increasing escalation configurations */
  escalations?: EscalationConfig[]
}

export type NotificationFilterType = {
  severities: Severity[]
  statuses: IncidentStatus[]
  onActions: IOTV_Notifiable_EventType[]
}

export interface INotificationSubscription<T extends SubscriptionState = SubscriptionState> {
  incidentProducingTypeId: string
  incidentEntityOrGroupingSk: string
  incidentEntityOrGroupingName?: string
  incidentInitiatorSk: string | undefined
  incidentType: AlertIncidentType | undefined
  incidentSubtype: string | undefined
  filter: NotificationFilterType

  subscriptionState: T

  deliveryPreferences: NotificationDeliveryConfig[]
}

export type NotificationConfig<T extends DeliveryType> = T extends DeliveryType.EMAIL
  ? SendEmailRequest
  : T extends DeliveryType.SMS
    ? PublishInput
    : T extends DeliveryType.MOBILE_PUSH
      ? MapLike<any>
      : MapLike<any>

/** @deprecated */
export type MessageTemplate = (MessagePlaceholders | string)[]

export type NotificationContent = {
  messageTitle?: string | undefined
  messageContent?: string | undefined
  /** @deprecated */
  messageTemplate?: MessageTemplate | undefined
  messageHTML?: string | undefined
}

export type NotificationSendRequest<T extends DeliveryType> = NotificationContent & {
  deliveryType: T
  recipients: ValidModelObjects<'User' | 'Customer'>
  destinations: string[]
  /** reference key will be used as pk of notification if present */
  referenceKey?: string
  incident?: Incident
  alertTrigger?: AlertTrigger<unknown>
}

export type NotificationType = NotificationContent & {
  deliveryType: DeliveryType
  emailDestinations: string[]
  smsDestinations: string[]
  timestamp: number
  success: boolean
  acknowledged: boolean
  MessageId?: string
  incidentSk?: string | undefined
}

export type NotificationSubscriptionDSKParams = {
  incidentEntityOrGroupingSk: string
  incidentType?: AlertIncidentType
  incidentSubtype?: string
  incidentInitiatorSk?: string
}

/**
 * DSK is a colon-separated list of:
 * - Entity / Group SK
 * - Incident type
 * - (Optional) Incident subtype
 * - Incident initiator SK
 *
 * If there is no incident subtype, this results in:
 * `entity type or group : ID : incident type : incident initiator type : ID`
 *
 * If there is an incident subtype, this results in:
 * `entity type or group : ID : incident type : ruleset type : ID : incident initiator type : ID`
 *
 * If there is no incident initiator, it the type and ID will be replaced with a single `undefined` instead
 */
export const getNotificationSubscriptionDSK = ({
  incidentEntityOrGroupingSk,
  incidentType,
  incidentSubtype,
  incidentInitiatorSk,
}: NotificationSubscriptionDSKParams): string => {
  return `${incidentEntityOrGroupingSk}:${incidentType}${incidentSubtype ? `:${incidentSubtype}` : ''}:${incidentInitiatorSk}`
}
